(ns ui.view.counterparty-diligence.responder-form.entity-list-editor
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.entities.counterparty-diligence.templates.util :as tu]
   [common.util :as cu]
   [re-frame.core :as rf]
   [ui.components.button.view :as button]
   [ui.view.counterparty-diligence.responder-form.model :as model]
   [ui.view.counterparty-diligence.responder-form.event :as event]
   [ui.view.counterparty-diligence.responder-form.sub :as sub]
   [ui.view.counterparty-diligence.responder-form.util :as c-util]
   [ui.view.sheets.event :as sheet-event]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.view.counterparty-diligence.responder-form.validation-error-text :as error-text]))

(defn entity-table-row
  [{:keys [row-data row-index field state-path row-validation-errors validation-errors? read-only? table-columns
           entity-type-label lang-kw item-template date-format set-as-ubo] :as args}]
  [:tr
   (->> table-columns
        (map-indexed
         (fn [col-index {:keys [field] :as question-config}]
           (let [value (if (= :editor.type/nationality-list (:editor-type question-config))
                         ;; resolves the legacy single nationality answer for
                         ;; rows saved before the multi-nationality field
                         (tu/nationality-rows row-data)
                         (get row-data field))
                 display-str (c-util/display-string (assoc question-config
                                                           :value value
                                                           :lang-kw lang-kw
                                                           :date-format date-format))]
             ^{:key (str row-index "-" col-index)}
             [:td
              {:class "p-2 border break-words"}
              [:div
               {:class "text-pretty"}
               display-str]]))))
   (if read-only?
     [:td
      {:class "p-2 border w-10"}
      [button/square-secondary-hollow
       {:on-click #(rf/dispatch [::sheet-event/open-sheet
                                 {:sheet-key  model/sheet-view-key
                                  :sheet-group :counterparty-diligence-form
                                  :props      {:sheet-title entity-type-label
                                               :lang-kw     lang-kw
                                               :template    item-template}
                                  :state      {:template        item-template
                                               :initial-answers row-data
                                               :answers         row-data}}])}
       [:i {:class "fal fa-eye"}]]]
     (let [{:keys [target-field default-ubo-category map-fields
                   target-item-template target-entity-label]} set-as-ubo
           already-ubo? @(rf/subscribe [::sub/already-ubo?
                                        {:state-path   state-path
                                         :target-field target-field
                                         :row-data     row-data}])
           show-ubo-button? (and set-as-ubo (not already-ubo?))]
       [:td
        {:class (if show-ubo-button? "p-2 border w-48" "p-2 border w-20")}
        [:div
         {:class "flex gap-2 items-center justify-center"}
         (when show-ubo-button?
           (let [mapped-ubo   (-> (select-keys row-data map-fields)
                                  (assoc :primary-ubo-category default-ubo-category))
                 target-label (-> target-entity-label
                                  (get lang-kw)
                                  cu/str-trim-lcase)]
             [button/sm-secondary-hollow
              {:tooltip  (loc/tr :set-as-ubo-long)
               :on-click #(rf/dispatch [::event/open-sheet-form
                                        {:create?     true
                                         :field       target-field
                                         :template    target-item-template
                                         :state-path  state-path
                                         :lang-kw     lang-kw
                                         :answers     mapped-ubo
                                         :sheet-title (loc/tr :add- [target-label])}])}
              [:i {:class "fal fa-user-plus pr-2"}]
              (loc/tr :set-as-ubo-short)]))

         [button/square-secondary-hollow
          {:on-click #(rf/dispatch [::event/open-sheet-form
                                    {:field       field
                                     :answers     row-data
                                     :row-index   row-index
                                     :template    item-template
                                     :state-path  state-path
                                     :sheet-title (loc/tr :edit- [entity-type-label])}])}
          [:i {:class "fal fa-pencil"}]]

         [button/square-secondary-hollow
          {:on-click (fn []
                       (rf/dispatch [::event/open-confirmation-modal
                                     {:state-path state-path
                                      :modal-type :remove-entity-list-entity
                                      :props      {:entity-type-label entity-type-label}
                                      :confirm-event [::event/remove-entity-list-entity
                                                      {:field      field
                                                       :row-index  row-index
                                                       :state-path state-path}]}]))}
          [:i {:class "fal fa-trash"}]]]]))

   (when validation-errors?
     [:td
      {:class "p-2 w-8"}
      (when row-validation-errors
        [tooltip/view
         [:i {:class "fal fa-exclamation-circle text-danger-regular"}]
         [:span (loc/tr :data-missing nil lang-kw)]])])])

(defn entity-table
  [{:keys [value table-columns state-path item-template field entity-type-label lang-kw read-only?
           validation-errors show-validation-errors?] :as args}]
  (let [validation-errors? (and show-validation-errors?
                                (->> validation-errors
                                     (remove nil?)
                                     seq))]
    [:table
     {:class "max-w-full overflow-hidden"}
     [:thead
      [:tr

       (->> table-columns
            (map (fn [col]
                   (let [label (get-in col [:label lang-kw])]
                     ^{:key (str label)}
                     [:th
                      {:class "border p-2 text-sm m-0 bg-base-regular"}
                      label]))))
       [:th {:class "p-2 border text-sm m-0 bg-base-regular"} ""]

       (when validation-errors?
         [:th ""])]]
     [:tbody
      (->> value
           (map-indexed
            (fn [row-index row-data]
              ^{:key (str row-index)}
              [entity-table-row
               (assoc args
                      :row-data row-data
                      :row-index row-index
                      :row-validation-errors (get validation-errors row-index)
                      :validation-errors? validation-errors?)])))]]))

(defn below-label-info-text
  [{:keys [min-items validation-errors entity-label entity-label-plural lang-kw show-validation-errors? info-text]}]
  (let [entity-label (-> entity-label
                         (get lang-kw)
                         cu/str-trim-lcase)
        entity-label-plural (-> entity-label-plural
                                (get lang-kw)
                                cu/str-trim-lcase)
        custom-info-text (cond
                           (string? info-text) info-text
                           (map? info-text)    (get info-text lang-kw))
        min-items-copy (cond
                         (not (string/blank? custom-info-text)) custom-info-text
                         (int? min-items)
                         (case min-items
                           1 (loc/tr :add-at-least-one-entity-type [entity-label])
                           2 (loc/tr :add-at-least-two-entity-types [entity-label-plural])
                           (loc/tr :add-at-least-n-entity-types [entity-label-plural])))]
    (cond
      (and show-validation-errors?
           (:general validation-errors))
      (let [too-few-items? (-> validation-errors
                               :general
                               (= :validation.error/too-few-items))]
        (if too-few-items?
          [error-text/view
           {:validation-errors min-items-copy}]
          [error-text/view
           {:validation-errors (:general validation-errors)}]))

      min-items-copy
      [:p
       {:class "text-sm m-0 text-base-light-text-paler"}
       min-items-copy])))

(defn view
  [{:keys [value label state-path field item-template max-items lang-kw read-only?
           validation-errors] :as props}]
  (let [entity-type-label (-> props
                              (get-in [:entity-label lang-kw])
                              cu/str-trim-lcase)
        can-add-items? (and (not read-only?)
                            (or (not max-items)
                                (< (count value) max-items)))]
    [:div
     {:class "flex flex-col gap-2"}
     [:div
      {:class "flex flex-col gap-px"}
      [:p
       {:class "text-semibold"}
       label]
      [below-label-info-text props]]

     (when (seq value)
       [entity-table
        (assoc props
               :entity-type-label entity-type-label
               :validation-errors (when (map? validation-errors)
                                    (:items validation-errors)))])

     (when can-add-items?
       [:div
        {:class "flex justify-start"}
        [button/sm-primary-solid
         {:on-click (fn []
                      (rf/dispatch [::event/open-sheet-form
                                    {:create?     true
                                     :field       field
                                     :template    item-template
                                     :state-path  state-path
                                     :sheet-title (loc/tr :add- [entity-type-label])}]))}
         [:i {:class "fal fa-plus"}]
         (loc/tr :add- [entity-type-label])]])]))
